import {
  CadendarConsts,
  TimeConstraint,
  timeConstraintSchema,
} from "cadendar-shared";
import { atom, useAtom } from "jotai";
import { formaters, utils } from "vincent-utils";

const { jours } = CadendarConsts;

type JoursContraintes = "openDays" | (typeof jours)[number];

type Contrainte =
  | UnavailableContrainte
  | WholeDayAvailableContrainte
  | BeforeContrainte
  | AfterContrainte
  | BetweenContrainte;

type UnavailableContrainte = {
  mode: "unavailable";
};

type WholeDayAvailableContrainte = {
  mode: "toute la journée";
};

type BeforeContrainte = {
  mode: "avant";
  firstTimeContrainte: string;
};

type AfterContrainte = {
  mode: "après";
  firstTimeContrainte: string;
};

type BetweenContrainte = {
  mode: "entre";
  firstTimeContrainte: string;
  secondTimeContrainte: string;
};

type ContraintesState = {
  [jour in (typeof jours)[number]]: Contrainte | null;
};

const isOkChecked = (state: ContraintesState, jour: JoursContraintes) => {
  if (jour === "openDays") {
    return Object.entries(state)
      .filter(([jour, _state]) => jour !== "samedi" && jour !== "dimanche")
      .map(([_jour, state]) => state)
      .every((contrainte) => {
        if (!contrainte) {
          return false;
        }
        return contrainte.mode !== "unavailable";
      });
  }
  const contrainte = state[jour];
  if (!contrainte) {
    return false;
  }
  return contrainte.mode !== "unavailable";
};

const isNoOkChecked = (state: ContraintesState, jour: JoursContraintes) => {
  if (jour === "openDays") {
    return false;
  }
  const contrainte = state[jour];
  if (!contrainte) {
    return false;
  }
  return contrainte.mode === "unavailable";
};

const getDefaultContrainte = () =>
  jours.reduce(
    (acc, jour) => ({ ...acc, [jour]: null }),
    {} as ContraintesState,
  );

const ContraintesStateAtom = atom<ContraintesState>(getDefaultContrainte());

export const useContraintes = () => {
  const [contraintes, setContraintes] = useAtom(ContraintesStateAtom);

  const onNoOkToggle = (jour: JoursContraintes) => {
    setContraintes((contraintes) => {
      if (Object.values(contraintes).every((contrainte) => !contrainte)) {
        return jours.reduce((acc, jourInternal) => {
          return {
            ...acc,
            [jourInternal]: {
              mode: jourInternal === jour ? "unavailable" : "toute la journée",
            },
          };
        }, {} as ContraintesState);
      }

      if (isNoOkChecked(contraintes, jour)) {
        return {
          ...contraintes,
          [jour]: {
            mode: "toute la journée",
          },
        };
      }

      return {
        ...contraintes,
        [jour]: {
          mode: "unavailable",
        },
      };
    });
  };

  const onOkToggle = (jour: JoursContraintes) => {
    setContraintes((contraintes) => {
      if (jour === "openDays") {
        if (isOkChecked(contraintes, "openDays")) {
          return jours
            .filter((jour) => jour !== "dimanche" && jour !== "samedi")
            .reduce((acc, jour) => {
              return {
                ...acc,
                [jour]: {
                  mode: "unavailable",
                },
              };
            }, {} as ContraintesState);
        }

        return jours
          .filter((jour) => jour !== "dimanche" && jour !== "samedi")
          .reduce((acc, jour) => {
            return {
              ...acc,
              [jour]: {
                mode: "toute la journée",
              },
            };
          }, {} as ContraintesState);
      }

      if (Object.values(contraintes).every((contrainte) => !contrainte)) {
        return jours.reduce((acc, jourInternal) => {
          return {
            ...acc,
            [jourInternal]: {
              mode: jourInternal === jour ? "toute la journée" : "unavailable",
            },
          };
        }, {} as ContraintesState);
      }

      if (isOkChecked(contraintes, jour)) {
        return {
          ...contraintes,
          [jour]: {
            mode: "unavailable",
          },
        };
      }

      return {
        ...contraintes,
        [jour]: {
          mode: "toute la journée",
        },
      };
    });
  };

  const onSelectContrainte = (
    jour: JoursContraintes,
    mode: Contrainte["mode"],
  ) => {
    setContraintes((contraintes) => {
      if (jour === "openDays") {
        return jours
          .filter((jour) => jour !== "dimanche" && jour !== "samedi")
          .reduce((acc, jour) => {
            return {
              ...acc,
              [jour]: {
                mode,
              },
            };
          }, {} as ContraintesState);
      }

      return {
        ...contraintes,
        [jour]: {
          mode,
        },
      };
    });
  };

  const onSelectFirstTimeContrainte = (
    jour: JoursContraintes,
    value: string,
  ) => {
    setContraintes((contraintes) => {
      if (jour === "openDays") {
        return jours
          .filter((jour) => jour !== "dimanche" && jour !== "samedi")
          .reduce((acc, jour) => {
            return {
              ...acc,
              [jour]: {
                ...acc[jour],
                firstTimeContrainte: value,
              },
            };
          }, contraintes);
      }

      return {
        ...contraintes,
        [jour]: {
          ...contraintes[jour],
          firstTimeContrainte: value,
        },
      };
    });
  };

  const onSelectSecondTimeContrainte = (
    jour: JoursContraintes,
    value: string,
  ) => {
    setContraintes((contraintes) => {
      if (jour === "openDays") {
        return jours
          .filter((jour) => jour !== "dimanche" && jour !== "samedi")
          .reduce((acc, jour) => {
            return {
              ...acc,
              [jour]: {
                ...acc[jour],
                secondTimeContrainte: value,
              },
            };
          }, contraintes);
      }

      return {
        ...contraintes,
        [jour]: {
          ...contraintes[jour],
          secondTimeContrainte: value,
        },
      };
    });
  };

  const contraintesConverted = Object.entries(contraintes).reduce(
    (acc, [jour, contrainte]) => {
      if (!contrainte) {
        return acc;
      }

      const convertedJour = {
        mode: contrainte.mode,
        ...("firstTimeContrainte" in contrainte
          ? utils.cleanObject({
              first: formaters.getHeureMinuteObjectFromHoraireString(
                contrainte.firstTimeContrainte,
              ),
            })
          : {}),
        ...("secondTimeContrainte" in contrainte
          ? utils.cleanObject({
              second: formaters.getHeureMinuteObjectFromHoraireString(
                contrainte.secondTimeContrainte,
              ),
            })
          : {}),
      };

      const validatedJourResult = timeConstraintSchema.safeParse(convertedJour);

      return {
        ...acc,
        ...(validatedJourResult.success
          ? { [jour]: validatedJourResult.data }
          : {}),
      };
    },
    {} as { [jour in (typeof jours)[number]]: TimeConstraint },
  );

  const showResetButton = !!contraintes;
  const onResetContraintes = () => setContraintes(getDefaultContrainte());

  return {
    contraintes,
    contraintesConverted,
    showResetButton,
    onResetContraintes,
    onNoOkToggle,
    onOkToggle,
    onSelectContrainte,
    onSelectFirstTimeContrainte,
    onSelectSecondTimeContrainte,
  };
};
